import { request, RequestError } from "./api";
import { getToken } from "./auth";
import { EVENTS_STORE, getAll, putMany, removeMany } from "../offline/idb";

const queueKey = "cerp-event-queue";
const sessionKey = "cerp-client-session";
const droppedKey = "cerp-event-dropped";
const deviceKey = "cerp-device-id";

// Bump when an event's shape or meaning changes, so analysis can tell old
// events from new ones.
export const EVENT_SCHEMA_VERSION = 2;
export const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

// Unsent events live in memory (so the sync API stays instant) and are
// mirrored to IndexedDB, which has no ~5 MB cap. localStorage is only the
// fallback where IndexedDB is unavailable. When the queue is full the oldest
// events are dropped, but never silently: the count is kept and reported to
// the server as an events_dropped event, and every event also carries a
// per-session sequence number (counting from 1 in each tab) so the server can
// see gaps even if that report never arrives.
export const MAX_QUEUED_EVENTS = 20000;
const seqKey = "cerp-session-seq";
const session = sessionStorage.getItem(sessionKey) || crypto.randomUUID();
sessionStorage.setItem(sessionKey, session);

export interface TrackContext {
  module_id?: string | null;
  item_id?: string | null;
  payload?: Record<string, unknown>;
}

interface QueuedEvent {
  event_uuid: string;
  phone_time: string;
  type: string;
  module_id: string | null;
  item_id: string | null;
  payload: Record<string, unknown>;
  connection: "online" | "offline";
  client_session: string;
  app_version: string;
  schema_version: number;
  device_id: string;
  session_seq: number;
}

// Stable per handset (survives logins and tabs), unlike the client session.
function deviceID(): string {
  try {
    const existing = localStorage.getItem(deviceKey);
    if (existing) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(deviceKey, created);
    return created;
  } catch {
    return "unknown";
  }
}
const device = deviceID();

function nextSeq(): number {
  try {
    // sessionStorage, not localStorage: each tab counts its own events, so
    // two tabs never race on one counter.
    const next = (Number(sessionStorage.getItem(seqKey)) || 0) + 1;
    sessionStorage.setItem(seqKey, String(next));
    return next;
  } catch {
    return 0;
  }
}

function envelope(type: string, context: TrackContext): QueuedEvent {
  return {
    event_uuid: crypto.randomUUID(),
    phone_time: new Date().toISOString(),
    type,
    module_id: context.module_id || null,
    item_id: context.item_id || null,
    payload: context.payload || {},
    connection: navigator.onLine ? "online" : "offline",
    client_session: session,
    app_version: APP_VERSION,
    schema_version: EVENT_SCHEMA_VERSION,
    device_id: device,
    session_seq: nextSeq(),
  };
}

let queue: QueuedEvent[] = [];
let useIDB = typeof indexedDB !== "undefined";
let ready: Promise<void> = hydrate();

export function track(type: string, context: TrackContext = {}): void {
  const event = envelope(type, context);
  queue.push(event);
  enforceCap();
  save([event], []);
  void flush();
}

// pendingEventCount backs the profile screen's sync status: how much
// activity is still sitting on this handset, unsent.
export function pendingEventCount(): number {
  return queue.length;
}

// Events this handset has lost and the server has not yet been told about:
// the counter not yet turned into an events_dropped event, plus any such
// events still waiting in the queue. Drops to 0 once the report is delivered.
export function unreportedDroppedCount(): number {
  return queue
    .filter((event) => event.type === "events_dropped")
    .reduce((total, event) => total + (Number(event.payload.count) || 0), droppedCount());
}

// Memory is the source of truth; IndexedDB (or localStorage as a fallback)
// is its durable copy. Writes are fire-and-forget: if IndexedDB fails we
// switch to localStorage for the rest of the session.
function save(added: QueuedEvent[], removed: string[]): void {
  if (useIDB) {
    const work = (async () => {
      if (added.length) await putMany(EVENTS_STORE, added);
      if (removed.length) await removeMany(EVENTS_STORE, removed);
    })();
    work.catch(() => {
      useIDB = false;
      persistLocal();
    });
    return;
  }
  persistLocal();
}

function enforceCap(): void {
  if (queue.length <= MAX_QUEUED_EVENTS) return;
  const dropped = queue.splice(0, queue.length - MAX_QUEUED_EVENTS);
  addDropped(dropped.length);
  save(
    [],
    dropped.map((event) => event.event_uuid)
  );
}

// Storage full (localStorage fallback): shed the oldest half until it fits,
// counting what goes.
function persistLocal(): void {
  for (;;) {
    try {
      localStorage.setItem(queueKey, JSON.stringify(queue));
      return;
    } catch {
      if (!queue.length) return;
      const shed = queue.splice(0, Math.ceil(queue.length / 2));
      addDropped(shed.length);
    }
  }
}

function readLocal(): QueuedEvent[] {
  try {
    return JSON.parse(localStorage.getItem(queueKey) || "[]") as QueuedEvent[];
  } catch {
    return [];
  }
}

// Loads whatever a previous page load left unsent: from IndexedDB, plus the
// old localStorage queue (older app versions, or the fallback) which is
// migrated across. Events tracked before this finishes are kept.
async function hydrate(): Promise<void> {
  const local = readLocal();
  let stored: QueuedEvent[] = [];
  if (useIDB) {
    try {
      stored = await getAll<QueuedEvent>(EVENTS_STORE);
    } catch {
      useIDB = false;
    }
  }
  const merged = new Map<string, QueuedEvent>();
  [...local, ...stored, ...queue].forEach((event) => merged.set(event.event_uuid, event));
  // Time order restores the queue; within one session the sequence breaks
  // ties. Array.sort is stable, so legacy events keep their order.
  queue = [...merged.values()].sort(
    (a, b) =>
      a.phone_time.localeCompare(b.phone_time) ||
      (a.client_session === b.client_session ? (a.session_seq || 0) - (b.session_seq || 0) : 0)
  );
  enforceCap();
  if (useIDB) {
    try {
      await putMany(EVENTS_STORE, queue);
      localStorage.removeItem(queueKey);
    } catch {
      useIDB = false;
      persistLocal();
    }
  } else {
    persistLocal();
  }
}

// Test hook: forget everything held in memory and in storage.
export async function resetEventQueueForTests(): Promise<void> {
  await ready;
  queue = [];
  if (useIDB)
    await removeMany(
      EVENTS_STORE,
      (await getAll<QueuedEvent>(EVENTS_STORE)).map((e) => e.event_uuid)
    );
  localStorage.clear();
  ready = Promise.resolve();
}

// One flush runs at a time. Every track() asks for a flush, and overlapping
// ones would send the same events twice and overwrite each other's view of
// the queue.
let inflight: Promise<void> | null = null;
let again = false;

export function flush(): Promise<void> {
  if (inflight) {
    again = true;
    return inflight;
  }
  inflight = (async () => {
    await ready;
    do {
      again = false;
      await drain();
    } while (again);
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function drain(): Promise<void> {
  while (getToken() && navigator.onLine) {
    reportDropped();
    const batch = queue.slice(0, 100);
    if (!batch.length) return;
    try {
      await request("/api/events/batch", {
        method: "POST",
        body: JSON.stringify({ events: batch }),
      });
    } catch (cause) {
      // Network trouble, a server error, an expired session: keep the events
      // and retry later. A batch the server has refused outright will never
      // be accepted, and left at the front of the queue it would block every
      // event behind it, so that one is dropped.
      if (!isPermanentRejection(cause)) return;
    }
    removeSent(batch);
  }
}

function isPermanentRejection(cause: unknown): boolean {
  if (!(cause instanceof RequestError)) return false;
  const { status } = cause;
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
}

// Filter the live queue rather than trusting a snapshot taken before the
// request: events tracked while it was in flight must survive.
function removeSent(batch: QueuedEvent[]): void {
  const sent = new Set(batch.map((event) => event.event_uuid));
  queue = queue.filter((event) => !sent.has(event.event_uuid));
  save([], [...sent]);
}

function droppedCount(): number {
  try {
    return Number(localStorage.getItem(droppedKey)) || 0;
  } catch {
    return 0;
  }
}

function addDropped(count: number): void {
  try {
    localStorage.setItem(droppedKey, String(droppedCount() + count));
  } catch {
    // Nowhere left to record it; the sequence-number gap still shows it.
  }
}

// Turns the persisted dropped counter into a queued event, once, so the
// server learns how many events this handset lost.
function reportDropped(): void {
  const count = droppedCount();
  if (!count) return;
  try {
    localStorage.removeItem(droppedKey);
  } catch {
    return;
  }
  // Added past the cap on purpose: capping here would drop this very event
  // and count it again, forever. The next track() trims back to the cap.
  const event = envelope("events_dropped", { payload: { count, cap: MAX_QUEUED_EVENTS } });
  queue.unshift(event);
  save([event], []);
}

window.addEventListener("online", () => void flush());
